import React from "react";
import i18n from "src/i18n";
import styled from "styled-components";
import { Editor, EditorProps, SyntheticEvent } from 'react-draft-wysiwyg';
import { EditorState } from "draft-js";
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { DiscardButton, SaveButton } from "./customButtons/customButtons";
import { convertToHTML, convertFromHTML } from "draft-convert";
import { theme } from "src/config";

// Separate language file for Swedish locales of the Editor
import { draft_editor_sv } from '../../../locales/draft_editor_sv';
import { Language } from "src/types";

export interface RichInputProps {
  value:        string,
  disabled?:    boolean
  editorProps?: EditorProps,
  onDiscard?     (): string | null,
  onShowToolbar? (): void,
  onHideToolbar? (): void,
  onSave?        (text: string): string | null,
}
export const RichInput = (props: RichInputProps): JSX.Element => {

  const { value, onSave, disabled, onDiscard, onShowToolbar, onHideToolbar, editorProps } = props;
  const [showToolbar,  setShowToolbar]  = React.useState<boolean>(false);
  const [state, setState] = React.useState<EditorState>(EditorState.createWithContent(convertFromHTML(value)));

  const editorRef = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    setState(EditorState.createWithContent(convertFromHTML(value)));
  }, [value]);

  const _onSave = (editorState: EditorState) => {
    const content = convertToHTML(editorState.getCurrentContent())
    onSave && onSave(content);
    (document.activeElement as HTMLDivElement).blur();
    setShowToolbar(false);
    return editorState;
  }

  const _onDiscard = () => { 
    const val = onDiscard && onDiscard();
    (document.activeElement as HTMLDivElement).blur();
    setShowToolbar(false);
    return EditorState.createWithContent(convertFromHTML(val));
  }



  return (
    <Container showToolBar={showToolbar} ref = {editorRef}>
      <Editor
        onEditorStateChange = {setState}
        editorState         = {state}
        wrapperClassName    = "wrapper-class"
        editorClassName     = "editor-class"
        toolbarClassName    = "toolbar-class"
        readOnly            = {disabled}
        localization        = {i18n.language === Language.SV ? {
          locale: "sv",
          translations: draft_editor_sv,
        } : undefined}
        toolbarHidden    = {!showToolbar}
        onFocus          = {() => {
          setShowToolbar(true);
          onShowToolbar && onShowToolbar();
        }}
        onBlur           = {(event: SyntheticEvent) => {
          console.log(event)
          if(value === convertToHTML(state.getCurrentContent()) && showToolbar) {
            setShowToolbar(false);
            onHideToolbar && onHideToolbar();
          }
          _onSave(state); // trying out to save.
        }}
        toolbar = {{
          options: [ "history", "inline", "textAlign", "list", "colorPicker"],
          history:   { inDropdown: false, className: "button-group" },
          inline:    { 
            className:  "button-group",
            options:    [ 'bold', 'italic', 'underline' ],
            inDropdown: false
          },
          textAlign:   { inDropdown: true},
          list:        { inDropdown: true },
          colorPicker: { inDropdown: false},
          link:        { inDropdown: false, options: ["link"]},
        }}
        toolbarCustomButtons={[
          <SaveButton    onClick={_onSave}    />,
          <DiscardButton onClick={_onDiscard} />
        ]}
        {...editorProps}
      />
    </Container>
  );
}

const Container = styled.div<{showToolBar: boolean}>`
  padding-bottom: 2rem;

  .wrapper-class {
    border: 1px solid ${theme.color.primaryBorder};
  }

  .rdw-editor-main {
    font-size:   ${theme.font.size.small};
    margin-left: 0.5em;
  }

  .rdw-link-modal-target-option {
    margin-bottom: 1rem;
  }

  .rdw-link-modal-btn {
    border-radius: 0.4rem;
    color:         ${theme.color.white};
    background:    ${theme.color.buttons.primary.background};

  :hover {
    box-shadow: none;
    background: ${theme.color.buttons.primary.hover};
  }

  :active {
    background: ${theme.color.buttons.primary.active};
    }
  }

  .rdw-editor-toolbar {
    border-radius: 0;
    border-top:    none;
    border-right:  none;
    border-left:   none;
    border-bottom: 1px solid ${theme.color.primaryBorder};  /* need to declare borders separately, otherwise it doesn't work */
  }

  .rdw-dropdown-wrapper {
    border:       1px solid ${theme.color.primaryBorder};
    margin-right: 0.5rem;
    margin-left:  0;
    
    :hover {
      box-shadow: none;
      background: ${theme.color.slate3};
    }

    :active {
      box-shadow: none;
      border: none;
    }
  }

  .rdw-link-wrapper {
    border-radius: 2px;

    .rdw-link-modal {
      padding-bottom: 2rem;
      height:         fit-content;
    }

    .rdw-option-wrapper {
      height:      100%;
      width:       3rem;
      margin-left: 0;
      border:      1px solid ${theme.color.primaryBorder};

    :active {
      outline: 2px solid ${theme.color.buttons.primary.active};
    }

    :hover {
      box-shadow: none;
      background: ${theme.color.slate3};
    }
  }
}

  .rdw-history-wrapper {

    .rdw-option-active {
      box-shadow: none;
      background-color: ${theme.color.slate5};
    }
    
    .rdw-option-wrapper {
      border-radius: 0;
      height:      100%;
      width:       3rem;
      margin:      0;
    
      .last-child {
        border: none;
      }

      :active {
        border: none;
        box-shadow: none;
      }

      :hover {
        box-shadow: none;
        background: ${theme.color.slate3};
      }
    }

  }

  .rdw-inline-wrapper {

    .rdw-option-active {
      box-shadow: none;
      background-color: ${theme.color.slate5};
    }
    
    .rdw-option-wrapper {
      border-radius: 0;
      height:      100%;
      width:       3rem;
      margin:      0;
    
      .last-child {
        border: none;
      }

      :active {
        border: none;
        box-shadow: none;
      }

      :hover {
        box-shadow: none;
        background: ${theme.color.slate3};
      }
    }
  }

  .rdw-colorpicker-wrapper {
    border-radius: 2px;
    margin-right:  0.5rem;

    .rdw-colorpicker-modal {
      width: 26rem;

    .rdw-colorpicker-modal-header {
      display:         flex;
      gap:             2rem;

      .rdw-colorpicker-modal-style-label {
        padding: 0;
      }
    }
    }

    .rdw-option-wrapper:not(.rdw-colorpicker-option) {
      height:       100%;
      width:        3rem;
      margin-right: 0;
      border:       1px solid ${theme.color.primaryBorder};

      :hover {
        box-shadow: none;
        background: ${theme.color.slate3};
    }

      :active {
        outline: 2px solid ${theme.color.buttons.primary.active};
    }
  }
}

  .toolbar-class {
    border-bottom: 1px solid ${theme.color.primaryBorder};
    display:       ${p => p.showToolBar ? undefined : "none"};
  }

  .button-group {
    border-radius: 2px;
    height:        3rem;
    border:        1px solid ${theme.color.primaryBorder};
    margin-right:  0.5rem;

    .rdw-option-wrapper {
      border: none;
    }
  }
`;
